<template>
	<!--	<app-alert v-if="!canEdit" theme="info" class="mt-5">-->
	<!--		This page is read-only. Editing requires the <strong>Edit Product Range</strong> permission.-->
	<!--	</app-alert>-->

	<app-title-bar
		:icon="['fas', 'fa-folder']"
		:subtitle="product?.type?.name"
		:title="product?.name">
		<template #icon>
			<product-category-icon
				:category="product?.type?.category"
				size="medium"></product-category-icon>
		</template>

		<app-button
			v-if="product"
			:to="`/product-data/product-categories/${product?.type?.category}`"
			class="ml-2"
			icon="fa-circle-left">
			Back
		</app-button>
	</app-title-bar>

	<template v-if="!loading && product">
		<app-panel title="Product Information">
			<template #header>
				<product-form
					v-if="canEdit"
					:product="product"
					@completed="productUpdated"></product-form>
			</template>

			<div class="flex gap-6">
				<div class="flex-1 min-w-0">
					<list-group-item key="type" label="Type" :value="product.type.name" compact />
					<list-group-item key="name" label="Name" :value="product.name" compact />
					<list-group-item key="summary" label="Summary" :value="product.summary" compact />
					<list-group-item
						key="description"
						label="Description"
						:value="formatText(product.description)"
						compact />
					<list-group-item
						key="features_benefits"
						label="Features & Benefits"
						:value="formatText(product.features_benefits)"
						compact />
				</div>
				<div v-if="product.image?.download_url" class="flex-shrink-0 w-56 flex justify-end">
					<img
						:src="product.image.download_url"
						:alt="product.name"
						class="max-h-64 w-full rounded border border-gray-200 object-contain" />
				</div>
			</div>
		</app-panel>

		<app-panel title="Documentation" class="mt-5">
			<template #header>
				<add-document v-if="canEdit" :product="product.id" @completed="load" />
			</template>
			<documents-table
				:key="`docs-${tableKey}`"
				:documents="allDocuments"
				show-level-column
				:product-id="product.id"
				:product-name="product.name"
				:readonly="!canEdit"
				:show-business-unit-column="false"
				@document-removed="load"
				@refresh-documents="load" />
		</app-panel>

		<app-panel title="Videos" class="mt-5">
			<template #header>
				<add-video-form
					v-if="canEdit"
					:product-id="product.id"
					:can-edit="canEdit"
					@video-added="load" />
			</template>
			<videos-table
				:product-id="product.id"
				:videos="product.videos"
				:can-edit="canEdit"
				@video-removed="load" />
		</app-panel>

		<app-panel title="Sub Groups" class="mt-5">
			<template #header>
				<div class="flex gap-2">
					<add-sub-group v-if="canEdit" :product-id="product.id" @completed="load" />
					<app-button
						v-if="canEdit && selectedSubGroups.length > 0"
						theme="danger"
						icon="trash"
						@click="showRemoveSubGroupModal = true">
						Delete Selected
					</app-button>
				</div>
			</template>
			<sub-groups-table
				:key="`sub-groups-${tableKey}`"
				v-model:selected="selectedSubGroups"
				:subgroups="subgroups"
				:loading="loading"
				filterable />
		</app-panel>
	</template>

	<div v-if="loading">
		<app-panel title="Product Information">
			<template #header>
				<div class="h-10 w-40 bg-gray-300 rounded animate-pulse mb-4"></div>
			</template>

			<div v-for="i in 5" :key="i" class="flex justify-between py-2">
				<div class="w-32 h-4 bg-gray-300 rounded animate-pulse"></div>
				<div class="flex-1 h-4 bg-gray-200 rounded animate-pulse ml-4"></div>
			</div>
		</app-panel>

		<app-panel title="Documentation" class="mt-5">
			<template #header>
				<div class="h-10 w-32 bg-gray-300 rounded animate-pulse mb-4"></div>
			</template>

			<div v-for="i in 2" :key="i" class="flex my-3 items-center animate-pulse">
				<div class="w-8 h-10 bg-gray-300 rounded"></div>
				<div class="flex flex-col ml-2 flex-1">
					<div class="h-4 w-1/3 bg-gray-200 rounded mb-1"></div>
					<div class="h-5 w-2/3 bg-gray-200 rounded"></div>
				</div>
				<div class="ml-5 flex gap-2">
					<div class="h-8 w-16 bg-gray-300 rounded"></div>
					<div class="h-8 w-16 bg-gray-300 rounded"></div>
				</div>
			</div>
		</app-panel>

		<app-panel title="Videos" class="mt-5">
			<div class="h-24 bg-gray-200 rounded animate-pulse"></div>
		</app-panel>

		<app-panel title="Sub Groups" class="mt-5">
			<template #header>
				<div class="h-10 w-20 bg-gray-300 rounded animate-pulse"></div>
			</template>
		</app-panel>
	</div>

	<confirm-dialog
		:open="showRemoveSubGroupModal"
		title="Remove Sub Group"
		button="Remove"
		theme="danger"
		@confirmed="removeSubGroup"
		@canceled="showRemoveSubGroupModal = false">
		<p>
			Are you sure you want to remove the following subgroups?
			<strong>{{ selectedSubGroups.map((sg) => sg.subgroup).join(', ') }}</strong>
		</p>
	</confirm-dialog>
</template>

<script lang="ts" setup>
	import { onMounted, ref, watch, computed } from 'vue'
	import { useRoute } from 'vue-router'
	import AppPanel from '@/components/layout/panel/AppPanel.vue'
	import ProductService from '@/services/product'
	import ProductCategoryService from '@/services/product-category'
	import SubgroupService from '@/services/subgroup'
	import AddDocument from '@/components/partials/documents/AddDocument.vue'
	import ProductCategoryIcon from '@/components/partials/icons/ProductCategoryIcon.vue'
	import ListGroupItem from '@/components/partials/ListGroupItem.vue'
	import type { Product, SubGroup } from '@/data/schema/product'
	import type { Document } from '@/data/schema/document'
	import ProductForm from '@/components/partials/product-data/ProductForm.vue'
	import DocumentsTable from '@/components/datatables/all/DocumentsTable.vue'
	import SubGroupsTable from '@/components/datatables/all/SubGroupsTable.vue'
	import useAuthStore from '@/stores/auth'
	import AddSubGroup from '@/components/partials/product-data/AddSubGroup.vue'
	import ConfirmDialog from '@/components/partials/dialogs/Confirm.vue'
	import useToaster from '@/stores/toaster'
	import AddVideoForm from '@/components/partials/product-data/AddVideoForm.vue'
	import VideosTable from '@/components/datatables/all/VideosTable.vue'

	const product = ref<any>()
	const loading = ref(true)

	const formatText = (text: string): string => {
		if (!text) return ''
		const lines = text.split('\n')
		let html = ''
		let inList = false

		for (const line of lines) {
			const trimmed = line.trim()
			const isBullet = /^[-*]\s+/.test(trimmed)

			if (isBullet) {
				if (!inList) {
					html += '<ul class="list-disc list-inside space-y-0.5">'
					inList = true
				}
				html += `<li>${trimmed.replace(/^[-*]\s+/, '')}</li>`
			} else {
				if (inList) {
					html += '</ul>'
					inList = false
				}
				if (trimmed) {
					html += `<p class="mb-1">${trimmed}</p>`
				}
			}
		}

		if (inList) html += '</ul>'
		return html
	}
	const route = useRoute()
	const tableKey = ref(0)

	const id = ref(typeof route.params.id === 'string' ? route.params.id : route.params.id[0])
	const authStore = useAuthStore()
	const canEdit = computed(() => authStore.isAbleTo(['edit-product-range']))
	const toaster = useToaster()

	const subgroups = ref<SubGroup[]>([])
	const selectedSubGroups = ref<SubGroup[]>([])
	const allDocuments = ref<Document[]>([])

	const showRemoveSubGroupModal = ref(false)
	const removingSubGroup = ref(false)

	const load = async () => {
		loading.value = true
		try {
			// 1. Fetch all data first
			const api = new ProductService()
			const productResponse = await api.get(id.value, {
				expand: [
					'type',
					'image',
					'documents.type',
					'documents.language',
					'documents.revisions',
					'documents.revisions.file',
					'subgroups',
					'videos'
				]
			}) // todo add videos
			const newProduct = productResponse.data
			const productDocuments = newProduct.documents

			const categoryDocsPromise = newProduct?.type?.category
				? new ProductCategoryService().get(newProduct.type.category, {
						expand: [
							'documents',
							'documents.type',
							'documents.language',
							'documents.revisions',
							'documents.revisions.file',
							'documents.links'
						]
					})
				: Promise.resolve(null)

			const subGroupsPromise =
				newProduct?.subgroups && newProduct.subgroups.length > 0
					? new SubgroupService().findManyByCodes(
							newProduct.subgroups.map((sg) => sg.subgroup)
						)
					: Promise.resolve(null)

			const [categoryResponse, subGroupResponse] = await Promise.all([
				categoryDocsPromise,
				subGroupsPromise
			])

			const categoryDocuments = categoryResponse ? categoryResponse.data.documents : []
			const newSubgroups = subGroupResponse
				? subGroupResponse.data.data.map((sg: any) => {
						const productSubgroup = newProduct.subgroups.find(
							(psg) => psg.subgroup === sg.code
						)
						return {
							id: productSubgroup?.id,
							subgroup: sg.code,
							name: sg.name,
							pgroup: sg.group
						}
					})
				: []

			// 2. Batch all state updates together
			product.value = newProduct
			subgroups.value = newSubgroups
			selectedSubGroups.value = []
			tableKey.value++

			const catDocs = categoryDocuments.map((d) => ({ ...d, level: 'Category' }))
			const prodDocs = productDocuments.map((d) => ({ ...d, level: 'Product' }))
			allDocuments.value = [...catDocs, ...prodDocs]

			loading.value = false
		} catch (error) {
			console.error('Error fetching product:', error)
			loading.value = false
		} finally {
			loading.value = false
		}
	}

	const productUpdated = (value: Product | undefined | false) => {
		if (value) {
			if (value.id && value.id !== id.value) {
				id.value = value.id
			}
			load()
		}
	}

	const removeSubGroup = async () => {
		if (!product.value || selectedSubGroups.value.length === 0) return

		removingSubGroup.value = true
		try {
			const api = new ProductService()
			const codesToRemove = selectedSubGroups.value.map((sg) => sg.subgroup)
			await api.removeSubGroupsByCodes(product.value.id, codesToRemove)

			toaster.success({ text: 'Sub group(s) removed successfully' })
			showRemoveSubGroupModal.value = false
			selectedSubGroups.value = []
			load()
		} catch (error) {
			console.error('Error removing sub group(s):', error)
			toaster.error({ text: 'Failed to remove sub group(s)' })
		} finally {
			removingSubGroup.value = false
		}
	}

	onMounted(() => {
		load()
	})

	watch(
		() => route.params.id,
		(newId) => {
			id.value = typeof newId === 'string' ? newId : newId[0]
			load()
		}
	)
</script>
